import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {toggleItemInList, useSelection} from '@ELib/functions/selection';
import {showSnackbar, SnackbarType} from '@ELib/functions/snackbar';
import {useArrowKeysLoadTriggerHelper} from '@ELib/utilities/hooks/arrowKeyLoadTriggerHelper';
import {
  useMountEffect,
  useNonMountEffect,
} from '@ELib/utilities/hooks/effectHelpers';
import {stopEvent} from '@ELib/utilities/hooks/keyboardHandler';
import {ShortKeyActionType} from '@ELib/utilities/types';

import {LoadTrigger} from '../LoadTrigger';

import {EListEntry} from './EListEntry';
import {handleDragEnd, handleDragStart} from './drag-handlers';
import {StyledList} from './styles';
import {
  EListEntryActionHandler,
  EListEntryClickHandler,
  EListEntryDragHandler,
  EListEntryDragParams,
  EListEntryId,
  EListItemEntry,
  EListProps,
  EListSingleAction,
} from './types';
import {
  buildSectionedItemList,
  getNextSelectableEntry,
  isActiveEntryChildItem,
  isEqualActiveItem,
} from './utilities';

/**
 * Flexible list component, which takes a custom list entry component.
 *
 * The entries can be navigated with up and down arrow keys.
 * Expand toggles can be expanded and collapsed with Keyboard's left and right arrows.
 */
// eslint-disable-next-line max-statements
export const EList = <T extends object>({
  placeholder: userPlaceholder,
  data,
  activeItem: userActiveItem,
  onActiveItemChange,
  expandedItems: userExpandedItems,
  onExpandedItemsChange,
  hasMoreBefore,
  hasMoreAfter,
  onLoadMoreBefore,
  onLoadMoreAfter,
  onTopTouch,
  onBottomTouch,
  shouldActivateOnContextClick,
  selectedItems: userSelectedItems,
  onSelectedItemsChange,
  controlledSelection = false,
  controlledExpansion = false,
  focusTrigger = false,
  scrollToElementTrigger = false,
  selectOnArrowNavigation = false,
  testId,
  listRef,
}: EListProps<T>): React.ReactElement => {
  const {t} = useTranslation('lib');
  const placeholder = userPlaceholder || t('placeholder.noContent');
  const internalRef = useRef<HTMLUListElement>(null);

  // Set initial active item (if available), then watch it for changes and
  // update the state accordingly.
  const [activeItem, setActiveItem] = useState(userActiveItem);
  // store active item that was last announced to the outside of the component
  const lastActiveItemRef = useRef(userActiveItem);

  const userExpandedSet = useMemo(
    () => new Set(userExpandedItems || []),
    [userExpandedItems]
  );
  const [expandedItems, setExpandedItems] =
    useState<Set<string>>(userExpandedSet);

  const {selectedItemIds, selectedItemsIdsAsString, setSelectedItemIds} =
    useSelection(userSelectedItems, controlledSelection, onSelectedItemsChange);

  useMountEffect(() => {
    if (isActiveEntryChildItem(userActiveItem)) {
      expandedItems.add(userActiveItem.parentId);
      setExpandedItems(new Set([...expandedItems]));
    }
  });

  useEffect(() => {
    if (!isEqualActiveItem(lastActiveItemRef.current, userActiveItem)) {
      setActiveItem(userActiveItem);
    }
  }, [userActiveItem]);

  useEffect(() => {
    // check is necessary to avoid update loops
    if (!isEqualActiveItem(activeItem, lastActiveItemRef.current)) {
      lastActiveItemRef.current = activeItem;
      onActiveItemChange?.(activeItem);
    }
  }, [activeItem, onActiveItemChange]);

  // match selected rows to new data (e.g. after sort or filter) and scroll to active item
  useEffect(
    () => {
      const selectedItemsMatchUserSelectedItems = [...selectedItemIds].some(
        (id) => !userSelectedItems?.includes(id)
      );

      if (!selectedItemsMatchUserSelectedItems) {
        return;
      }
      const verifiedSelectedItemIds = new Set<EListEntryId>();
      selectedItemIds.forEach((selectedItemId) => {
        const itemForId = data.find(({id}) => id === selectedItemId);
        if (itemForId) {
          verifiedSelectedItemIds.add(selectedItemId);
        }
      });

      setSelectedItemIds(verifiedSelectedItemIds);
    },
    // `selectedItemIds` is missing intentionally to avoid useEffect loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, selectedItemsIdsAsString]
  );

  const activeItemIndex = useMemo(
    () =>
      data.findIndex(
        (item) =>
          item.id === activeItem ||
          (isActiveEntryChildItem(activeItem) &&
            item.id === activeItem.parentId)
      ),
    [activeItem, data]
  );

  const selectedItemIndex = useMemo(
    () => data.findIndex((item) => selectedItemIds.has(item.id)),
    [selectedItemIds, data]
  );

  const activeEntry = useMemo(
    () => (activeItemIndex > -1 ? data[activeItemIndex] : undefined),
    [activeItemIndex, data]
  );

  const isEntryExpanded = useCallback(
    ({id}: EListItemEntry<T>) =>
      (controlledExpansion ? userExpandedSet : expandedItems).has(id),
    [controlledExpansion, userExpandedSet, expandedItems]
  );

  const checkIsDraggable = useCallback(
    (entry: EListItemEntry<T>) =>
      Boolean(
        entry.options?.dragDataList?.length || entry.options?.onDragStart
      ),
    []
  );

  const onRowDragStart: EListEntryDragHandler<T> = useCallback(
    ({dragEvent, activeEntry: activeDragEntry}: EListEntryDragParams<T>) =>
      handleDragStart({
        dragEvent,
        activeEntry: activeDragEntry,
        entries: data,
        selectedEntryIds: selectedItemIds,
      }),
    [data, selectedItemIds]
  );

  const onRowDragEnd: EListEntryDragHandler<T> = useCallback(
    ({dragEvent}: EListEntryDragParams<T>) => handleDragEnd({dragEvent}),
    []
  );

  // holds the actions callable on an entry
  const actionsHandler: EListEntryActionHandler<T> = useActionsHandler(
    data,
    selectedItemIds
  );

  // tabindex to fetch the correct element that can also be focused by keyboard
  const getEntryRef = (elementId: string, withTabIndex = true) => {
    let query = `:scope > li[data-id="${elementId}"]`;
    if (withTabIndex) {
      query += ' [tabindex]';
    }
    return internalRef.current?.querySelector(query);
  };

  const scrollIntoView = useCallback((entryId: string, smooth = true) => {
    const entryRef = getEntryRef(entryId);
    if (entryRef) {
      entryRef.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: smooth ? 'nearest' : 'center',
        inline: 'nearest',
      });
    }
  }, []);

  const focusEntry = (entryId: string) => {
    const entryRef = getEntryRef(entryId);
    if (entryRef) {
      (entryRef as HTMLElement).focus();
    }
  };

  const scrollEntryBelowStickyHeader = useCallback(
    (entryId: string, stickyHeaderId: string | undefined) => {
      if (!stickyHeaderId) {
        return;
      }

      const entryRef = getEntryRef(entryId);
      const stickyHeaderRef = getEntryRef(stickyHeaderId, false);
      if (entryRef && stickyHeaderRef) {
        const headerBottom = stickyHeaderRef.getBoundingClientRect().bottom;
        const entryTop = entryRef.getBoundingClientRect().top;
        if (headerBottom > entryTop) {
          internalRef.current?.scrollBy({
            top: entryTop - headerBottom,
            behavior: 'smooth',
          });
        }
      }
    },
    []
  );

  /** Highlights the selected row and ensures it is within the viewport. */
  const activateEntry = useCallback(
    (entry: EListItemEntry<T> | undefined, childEntry?: EListItemEntry<T>) => {
      if (!entry) {
        return;
      }

      if (!childEntry) {
        setActiveItem(entry.id);
      } else {
        setActiveItem({
          parentId: entry.id,
          childId: childEntry.id,
        });
      }
      scrollIntoView(entry.id);
    },
    [scrollIntoView]
  );

  const didScrollToElement = useRef(false);
  useEffect(() => {
    const index = selectOnArrowNavigation ? selectedItemIndex : activeItemIndex;
    if (index < 0 || data.length < 1 || didScrollToElement.current) {
      return;
    }

    const item = data[index];
    if (item) {
      scrollIntoView(item.id, false);
      didScrollToElement.current = true;
    }

    // we only want to focus the element when actively triggered via scrollToElementTrigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToElementTrigger, data]);

  useNonMountEffect(() => {
    if (currentNavigationEntry) {
      if (selectOnArrowNavigation) {
        updateSelection({
          entry: currentNavigationEntry,
          shiftPressed: false,
          metaPressed: false,
          backward: false,
        });
      } else {
        activateEntry(currentNavigationEntry);
      }
      focusEntry(currentNavigationEntry.id);
    }
    // we only want select and/or set active on focus trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTrigger]);

  const currentNavigationItemIndex = useMemo(() => {
    const index = selectOnArrowNavigation ? selectedItemIndex : activeItemIndex;
    return index < 0 ? 0 : index;
  }, [activeItemIndex, selectOnArrowNavigation, selectedItemIndex]);

  const currentNavigationEntry = useMemo(() => {
    if (data.length === 0) {
      return;
    }

    return data[currentNavigationItemIndex];
  }, [currentNavigationItemIndex, data]);

  const activateChildEntryByIndex = useCallback(
    async (entry: EListItemEntry<T> | undefined, childEntryIndex: number) => {
      const childEntries = (await entry?.options?.expansion?.()) || [];
      activateEntry(entry, childEntries.at(childEntryIndex));
    },
    [activateEntry]
  );

  const changeActiveChildItem = useCallback(
    async (change: -1 | 1, nextEntry?: EListItemEntry<T>) => {
      if (isActiveEntryChildItem(activeItem)) {
        const childEntries = (await activeEntry?.options?.expansion?.()) || [];
        const childEntryIndex = childEntries.findIndex(
          ({id}) => id === activeItem.childId
        );
        if (change === 1 && childEntryIndex === childEntries.length - 1) {
          // `activeItem` is the last child item. Activate next item.
          activateEntry(nextEntry);
        } else if (change === -1 && childEntryIndex === 0) {
          // `activeItem` is the first child item. Activate parent item.
          activateEntry(activeEntry);
        } else {
          // Activate next/preceding child item.
          activateEntry(activeEntry, childEntries[childEntryIndex + change]);
        }
      }
    },
    [activateEntry, activeEntry, activeItem]
  );

  const updateSelection = useCallback(
    ({
      entry,
      shiftPressed,
      metaPressed,
      backward,
    }: {
      entry: EListItemEntry<T> | undefined;
      shiftPressed: boolean;
      metaPressed: boolean;
      backward: boolean;
    }): void => {
      let selectableEntry = entry;
      if (entry && entry.options?.isActivatable === false) {
        const currentIndex = data.findIndex((item) => item.id === entry.id);
        selectableEntry = getNextSelectableEntry(data, currentIndex, backward);
      }

      if (!selectableEntry) {
        return;
      }

      let selectedItems: string[] = [];

      if (!metaPressed && !shiftPressed) {
        selectedItems = [selectableEntry.id];
      } else if (metaPressed && !shiftPressed) {
        selectedItems = toggleItemInList(
          [...selectedItemIds],
          selectableEntry.id
        );
      } else if (shiftPressed) {
        const indexOfClicked = data.findIndex(
          (item) => item.id === selectableEntry?.id
        );
        if (indexOfClicked === -1) {
          return;
        }
        let indexOfActive = 0;
        if (!isActiveEntryChildItem(activeItem)) {
          indexOfActive = data.findIndex((item) => item.id === activeItem);
        }
        if (indexOfActive !== -1) {
          const lowerBoundIndex = Math.min(indexOfClicked, indexOfActive);
          const upperBoundIndex = Math.max(indexOfClicked, indexOfActive);
          selectedItems = [...selectedItemIds];

          const items = data
            .slice(lowerBoundIndex, upperBoundIndex + 1)
            .filter((item) => item.options?.isSelectable !== false)
            .map((item) => item.id);
          selectedItems = [...selectedItemIds, ...items];
        }
      }
      setSelectedItemIds(new Set(selectedItems));
      scrollIntoView(selectableEntry.id);
    },
    [activeItem, data, scrollIntoView, selectedItemIds, setSelectedItemIds]
  );

  const {handleLoadFinished, triggerLoadMore} = useArrowKeysLoadTriggerHelper(
    internalRef.current,
    internalRef.current
  );

  /** Changes the active entry relative to the current one and selects it, i.e. it activates the next or previous one. */
  const changeActiveItem = useCallback(
    (change: -1 | 1, shiftPressed: boolean, metaPressed: boolean) => {
      const nextItem = selectOnArrowNavigation
        ? getNextSelectableEntry(data, currentNavigationItemIndex, change < 0)
        : data[currentNavigationItemIndex + change];

      const isMainItemActive = typeof activeItem === 'string';
      if (isMainItemActive || selectOnArrowNavigation) {
        if (!nextItem) {
          if ((change < 0 && hasMoreBefore) || (change > 0 && hasMoreAfter)) {
            triggerLoadMore(change, shiftPressed, metaPressed);
          }
          if (change < 0 && !hasMoreBefore) {
            onTopTouch?.();
          }
          if (change > 0 && !hasMoreAfter) {
            onBottomTouch?.();
          }
        } else if (
          (change === 1 && !expandedItems.has(activeItem as string)) ||
          (change === -1 && !expandedItems.has(nextItem.id))
        ) {
          if (!selectOnArrowNavigation) {
            activateEntry(nextItem);
          }
          updateSelection({
            entry: nextItem,
            shiftPressed,
            metaPressed,
            backward: change < 0,
          });
          scrollEntryBelowStickyHeader(nextItem.id, nextItem.stickyHeaderId);
        } else {
          // The active/next item is expanded. Activate first/last child item.
          const [entry, childEntryIndex] =
            change === 1 ? [activeEntry, 0] : [nextItem, -1];
          activateChildEntryByIndex(entry, childEntryIndex);
        }
      } else {
        changeActiveChildItem(change, nextItem);
      }
    },
    [
      currentNavigationItemIndex,
      activeItem,
      data,
      hasMoreBefore,
      hasMoreAfter,
      onTopTouch,
      onBottomTouch,
      expandedItems,
      selectOnArrowNavigation,
      updateSelection,
      activateEntry,
      activeEntry,
      activateChildEntryByIndex,
      changeActiveChildItem,
      scrollEntryBelowStickyHeader,
      triggerLoadMore,
    ]
  );

  const onEntryClick: EListEntryClickHandler<T> = useCallback(
    (
      event: React.MouseEvent,
      entry: EListItemEntry<T>,
      childEntry?: EListItemEntry<T>
    ) => {
      if (entry.options?.isActivatable === false) {
        return;
      }

      const metaPressed =
        event.getModifierState('Control') || event.getModifierState('Meta');
      const shiftPressed = event.getModifierState('Shift');

      if (!metaPressed && !shiftPressed) {
        activateEntry(entry, childEntry);
      }

      if (childEntry || !entry.options?.isSelectable) {
        return;
      }

      const isRightMouseClickWithinSelection =
        event.type === 'contextmenu' && selectedItemIds.has(entry.id);
      if (isRightMouseClickWithinSelection) {
        return;
      }
      updateSelection({
        entry,
        shiftPressed,
        metaPressed,
        backward: false,
      });
    },
    // disable rule as selectedItemIdsAsString already do the job
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activateEntry,
      activeItem,
      selectedItemsIdsAsString,
      setSelectedItemIds,
      data,
    ]
  );

  const toggleEntry = useCallback(
    (entry: EListItemEntry<T>, value?: boolean) => {
      const toggleValue = value !== undefined ? value : !isEntryExpanded(entry);
      if (toggleValue) {
        expandedItems.add(entry.id);
      } else {
        expandedItems.delete(entry.id);
      }
      setExpandedItems(new Set([...expandedItems]));
      onExpandedItemsChange?.([...expandedItems]);
    },
    [expandedItems, isEntryExpanded, onExpandedItemsChange]
  );

  const allSelectableItemIds = useMemo(
    () =>
      data.reduce<string[]>((idList, item) => {
        if (item.options?.isSelectable) {
          return [...idList, item.id];
        }
        return idList;
      }, []),
    [data]
  );

  const toggleSelectAll = useCallback(() => {
    const itemIds =
      allSelectableItemIds.length === selectedItemIds.size
        ? []
        : allSelectableItemIds;
    setSelectedItemIds(new Set(itemIds));
  }, [allSelectableItemIds, selectedItemIds.size, setSelectedItemIds]);

  const executeTypedMultiAction = useCallback(
    (actionType: ShortKeyActionType) => {
      // check action type and if action is active
      const selectedRawItemsWithAction = data
        .filter((item) => selectedItemIds.has(item.id))
        .map((item) => ({
          raw: item.raw,
          action: item.options?.multiActions?.find(
            (value) => value.type === actionType
          ),
        }))
        .filter((item) => Boolean(item.action));

      if (selectedRawItemsWithAction.length === 0) {
        return;
      }

      const selectedItemsInactive = selectedRawItemsWithAction.map(
        (row) => row.action?.inactive
      );
      const disabledAction = selectedItemsInactive.includes(true);

      if (disabledAction) {
        showSnackbar({
          text: t('errors.cannotExecuteAction'),
          type: SnackbarType.INFO,
        });
      } else {
        selectedRawItemsWithAction[0]?.action?.callback?.(
          selectedRawItemsWithAction.map((item) => item.raw)
        );
      }
    },
    [data, selectedItemIds, t]
  );

  const keyDownHandler = useCallback(
    (event: React.KeyboardEvent) => {
      const {key} = event;
      const hasModifier = event.altKey || event.ctrlKey || event.metaKey;

      if (['ArrowDown', 'ArrowUp'].includes(key)) {
        stopEvent(event);
        const change = key === 'ArrowDown' ? +1 : -1;
        // ctrl key does not make sense with keyboard navigation and is overridden to false
        changeActiveItem(change, event.shiftKey, false);
      } else if (activeEntry && ['ArrowRight', 'ArrowLeft'].includes(key)) {
        stopEvent(event);
        const toggle = key === 'ArrowRight';
        toggleEntry(activeEntry, toggle);
        // In case the active item is a child item. This parent item should be activated.
        activateEntry(activeEntry);
      } else if (hasModifier && key === 'a') {
        stopEvent(event);
        toggleSelectAll();
      } else if (key === 'Backspace' || key === 'Delete') {
        stopEvent(event);
        executeTypedMultiAction(ShortKeyActionType.DELETE);
      } else if (key === 'Enter' || key === ' ') {
        stopEvent(event);
        if (data[currentNavigationItemIndex]?.options?.isSelectable) {
          activateEntry(data[currentNavigationItemIndex]);
        }
      }
    },
    [
      activateEntry,
      activeEntry,
      changeActiveItem,
      currentNavigationItemIndex,
      data,
      executeTypedMultiAction,
      toggleEntry,
      toggleSelectAll,
    ]
  );

  const renderItems = useMemo(() => buildSectionedItemList(data), [data]);

  return (
    <StyledList
      ref={(ref) => {
        if (!ref) {
          return;
        }
        (internalRef as React.MutableRefObject<HTMLUListElement>).current = ref;

        if (listRef) {
          (listRef as React.MutableRefObject<HTMLUListElement>).current = ref;
        }
      }}
      className='EList'
      data-testid={testId ? `EList-${testId}` : undefined}
      tabIndex={0}
      onKeyDown={keyDownHandler}
    >
      {data.length === 0 && placeholder && (
        <li className='placeholder noHover'>{placeholder}</li>
      )}
      {hasMoreBefore !== undefined && (
        <LoadTrigger
          dataSize={data.length}
          hasMore={hasMoreBefore}
          listRef={internalRef}
          isTop
          onLoad={async () => {
            await onLoadMoreBefore?.();
          }}
          onLoadFinished={handleLoadFinished}
        />
      )}
      {renderItems.map((item) => (
        <EListEntry
          key={item.id}
          actions={actionsHandler}
          activeItem={activeItem}
          entry={item}
          isDraggable={checkIsDraggable(item)}
          isExpanded={isEntryExpanded(item)}
          isSelected={selectedItemIds.has(item.id)}
          shouldActivateOnContextClick={shouldActivateOnContextClick}
          onDropAccept={item.options?.onDropAccept}
          onEntryClick={onEntryClick}
          onEntryDragEnd={onRowDragEnd}
          onEntryDragStart={onRowDragStart}
          onToggleExpansion={toggleEntry}
        />
      ))}
      {hasMoreAfter !== undefined && (
        <LoadTrigger
          dataSize={data.length}
          hasMore={hasMoreAfter}
          listRef={internalRef}
          onLoad={async () => {
            await onLoadMoreAfter?.();
          }}
          onLoadFinished={handleLoadFinished}
        />
      )}
    </StyledList>
  );
};

type ActionHandler<T extends object> = (
  entry: EListItemEntry<T>
) => EListSingleAction<T>[];

/** Simplifies the logic used to create the callback function which in turn returns the allowed actions on an entry */
export const useActionsHandler = <T extends object>(
  entries: EListItemEntry<T>[],
  selectedItemIds: Set<string>
): ActionHandler<T> => {
  const selectedItemsIdsAsString = [...selectedItemIds].sort().join(',');
  return useCallback(
    (entry: EListItemEntry<T>) => {
      const {singleActions, multiActions} = entry.options || {};
      if (singleActions?.length) {
        return singleActions;
      }

      if (multiActions?.length) {
        const selectedEntries = entries.filter((possiblySelectedEntry) =>
          selectedItemIds.has(possiblySelectedEntry.id)
        );
        const selectedEntriesRaw = selectedEntries.map(
          (selectedEntry) => selectedEntry.raw
        );
        return multiActions.map(
          (action) =>
            ({
              ...action,
              // The entry component only passes its own object to the action handlers.
              // But as we want to pass *all* the selected, we patch the callback handler.
              callback: () => action.callback?.(selectedEntriesRaw),
              subActions: action.subActions?.map((subAction) => ({
                ...subAction,
                callback: () => subAction.callback?.(selectedEntriesRaw),
              })),
            }) as EListSingleAction<T>
        );
      }
      return [];
    },
    // line disabled because selectedItemIds is stringed together to get better control over refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entries, selectedItemsIdsAsString]
  );
};
